import { isEmpty } from 'lodash-es';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingHandler } from 'client/tracking/handler';
import { TrackingConstant } from 'client/tracking/constant';
import { getQuery } from 'client/utils/location';
import { getOfferExpirationRecency } from 'site-modules/shared/utils/appraisal/common-utils';
import {
  APPRAISAL_PAGE_CREATIVE_ID,
  PARTNER_OFFER_TRACKING_CREATIVE_ID,
  PARTNER_OFFER_CONDITIONS,
  PARTNER_OFFER_CONDITION_VIEW_TRACKING,
  LOCATION_AND_STYLE,
  COLORS_AND_OPTIONS,
  CONDITION_AND_MILEAGE,
} from 'site-modules/shared/constants/appraisal/appraisal';

const EVENT_DATA = {
  BUTTON_CLICK: {
    action_category: TrackingConstant.USER_ACTION_CATEGORY,
    action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
  },
  USER_INPUT: {
    action_category: TrackingConstant.USER_ACTION_CATEGORY,
    action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
  },
};

export const fireAbTestBucketing = modsData => {
  if (!isEmpty(modsData) && modsData.source !== 'match_not_found') {
    const abTestBucketing = modsData?.abTestBucketing ?? [];
    const value = abTestBucketing.length ? abTestBucketing.join(',') : null;
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
        action_name: TrackingConstant.ACTION_RECEIVE_EPO,
        subaction_name: TrackingConstant.MODS_AB_TEST_BUCKETING,
        action_cause: TrackingConstant.PAGE_LOAD_CAUSE,
        creative_id: APPRAISAL_PAGE_CREATIVE_ID,
        value,
      },
    });
  }
};

export const fireSubmodelContainsCab = vehicle => {
  const {
    make: { slug: makeSlug },
    model: { slug: modelSlug },
    year,
  } = vehicle;
  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
    event_data: {
      action_category: TrackingConstant.USER_ACTION_CATEGORY,
      action_name: TrackingConstant.ACTION_APPRAISE_VEHICLE,
      subaction_name: TrackingConstant.SUBACTION_SUBMODEL_CONTAINS_CAB,
      action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
      creative_id: APPRAISAL_PAGE_CREATIVE_ID,
      value: `${year} ${makeSlug} ${modelSlug}`,
    },
  });
};

export const fireAppraisalPrefillTracking = modsData => {
  if (!isEmpty(modsData)) {
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_END,
      event_data: {
        action_name: TrackingConstant.ACTION_APPRAISE_VEHICLE,
        action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
        action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
        subaction_name: TrackingConstant.SUBACTION_APPRAISAL_PREFILL,
        creative_id: APPRAISAL_PAGE_CREATIVE_ID,
        value: modsData.source,
      },
    });
  }
};

export const fireOfferExpirationRecencyTracking = (location, modsData) => {
  if (!getQuery(location).vin || isEmpty(modsData)) return;

  const value = getOfferExpirationRecency(modsData);
  if (value === null) return;

  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_END,
    event_data: {
      action_name: TrackingConstant.ACTION_RECEIVE_EPO,
      action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
      action_cause: TrackingConstant.PAGE_LOAD_CAUSE,
      subaction_name: TrackingConstant.SUBACTION_OFFER_EXPIRATION_RECENCY,
      creative_id: PARTNER_OFFER_TRACKING_CREATIVE_ID,
      value,
    },
  });
};

export const fireMinMaxRangeTracking = ({ rough, clean, outstanding }) => {
  [
    { condition: rough, type: 'min' },
    { condition: outstanding || clean, type: 'max' },
  ].forEach(({ condition, type }) => {
    TrackingHandler.fireEvent(`partner_appraisal_range_${type}`, {
      event_data: {
        action_category: TrackingConstant.USER_ACTION_CATEGORY,
        action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
        creative_id: PARTNER_OFFER_TRACKING_CREATIVE_ID,
        value: condition,
      },
    });
  });
};

export const fireActiveStepEvents = stepId => {
  if (stepId === PARTNER_OFFER_CONDITIONS) {
    EventToolbox.fireTrackAction(PARTNER_OFFER_CONDITION_VIEW_TRACKING);
  }
};

export const fireActiveStepEndEvents = (
  stepId,
  {
    style,
    exteriorColorName,
    interiorColorName,
    selectedOptionsCount,
    selectedLowValueOptionsCount,
    condition,
    mileage,
    creativeId,
    userSelectedPreviousStep,
    zipCode,
    vehicleYear,
    transmissionType,
    isTransmissionTypeSelectedByUser,
  }
) => {
  if (stepId === CONDITION_AND_MILEAGE) {
    TrackingHandler.fireEvent('submit_mileage', {
      event_data: {
        ...EVENT_DATA.BUTTON_CLICK,
        creative_id: creativeId,
        value: mileage,
      },
    });
    TrackingHandler.fireEvent('submit_condition', {
      event_data: {
        ...EVENT_DATA.BUTTON_CLICK,
        creative_id: creativeId,
        value: condition,
      },
    });
  }
  if (stepId === LOCATION_AND_STYLE) {
    TrackingHandler.fireEvent('submit_style', {
      event_data: {
        ...EVENT_DATA.BUTTON_CLICK,
        creative_id: creativeId,
        value: style,
      },
    });
    TrackingHandler.fireEvent('submit_year', {
      event_data: {
        ...EVENT_DATA.BUTTON_CLICK,
        creative_id: creativeId,
        value: vehicleYear,
      },
    });
    if (zipCode && !userSelectedPreviousStep) {
      TrackingHandler.fireEvent('submit_zipcode', {
        event_data: {
          ...EVENT_DATA.BUTTON_CLICK,
          creative_id: creativeId,
          value: zipCode,
        },
      });
    }
  }
  if (stepId === COLORS_AND_OPTIONS) {
    if (exteriorColorName) {
      TrackingHandler.fireEvent('submit_exterior_color', {
        event_data: {
          ...EVENT_DATA.BUTTON_CLICK,
          creative_id: creativeId,
          value: exteriorColorName,
        },
      });
    }

    if (interiorColorName) {
      TrackingHandler.fireEvent('submit_interior_color', {
        event_data: {
          ...EVENT_DATA.BUTTON_CLICK,
          creative_id: creativeId,
          value: interiorColorName,
        },
      });
    }

    TrackingHandler.fireEvent('submit_options', {
      event_data: {
        ...EVENT_DATA.BUTTON_CLICK,
        creative_id: creativeId,
        value: selectedOptionsCount,
      },
    });
    if (selectedLowValueOptionsCount) {
      TrackingHandler.fireEvent('submit_low_value_options', {
        event_data: {
          ...EVENT_DATA.BUTTON_CLICK,
          creative_id: creativeId,
          value: selectedLowValueOptionsCount,
        },
      });
    }
    if (isTransmissionTypeSelectedByUser) {
      TrackingHandler.fireEvent('submit_transmission', {
        event_data: {
          ...EVENT_DATA.BUTTON_CLICK,
          creative_id: creativeId,
          value: transmissionType,
        },
      });
    }
  }
  if (stepId === PARTNER_OFFER_CONDITIONS) {
    TrackingHandler.fireEvent('partner_condition_questions_validation', {
      event_data: {
        ...EVENT_DATA.BUTTON_CLICK,
        creative_id: creativeId,
        value: 'success',
      },
    });
  }
};

export const fireActiveStepEndInvalidatedEvents = (stepId, { creativeId }) => {
  if (stepId === PARTNER_OFFER_CONDITIONS) {
    TrackingHandler.fireEvent('partner_condition_questions_validation', {
      event_data: {
        ...EVENT_DATA.BUTTON_CLICK,
        creative_id: creativeId,
        value: 'failure',
      },
    });
  }
};
